import type { ReactElement, ReactNode } from 'react'
import { Checkbox } from 'app/components/ui/checkbox'
import { useMediaQuery } from 'app/hooks/useMediaQuery'
import { cn } from 'app/lib/utils'
import type { ListBoardCardSelect } from './boardTypes'

/**
 * The shell every board card shares: the look, native drag with the id on the drag itself, the
 * marker a finger lifts by, and opening. It knows nothing about what a card shows.
 */
export function ListCard({
  id,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onPress,
  liftable = true,
  href,
  actions,
  className,
  select,
  name,
  children,
}: {
  id: string | undefined
  dragging: boolean
  onDragStart: () => void
  onDragEnd: () => void
  onOpen: () => void
  onPress?: () => void
  liftable?: boolean
  href?: string
  actions?: ReactNode
  className?: string
  /** Set while the board is selecting: a press picks the card, and nothing drags or opens. */
  select?: ListBoardCardSelect
  /** Who the card is, for its checkbox's name. */
  name?: string
  children: ReactNode
}): ReactElement {
  const coarsePointer = useMediaQuery(`(pointer: coarse)`)
  if (select)
    return (
      <div
        onClick={select.onToggle}
        className={cn(
          `shrink-0 cursor-pointer select-none rounded-xl border bg-surface shadow-elevation-1 transition`,
          select.selected ? `border-primary-500 ring-1 ring-primary-500` : `border-default hover:border-strong`,
          className,
        )}
      >
        <div className="flex items-start gap-2.5">
          <Checkbox
            aria-label={`Select ${name ?? `this card`}`}
            className="mt-0.5"
            checked={select.selected}
            // The card's own click already toggles; letting this one through would undo it.
            onClick={(event) => event.stopPropagation()}
            onCheckedChange={select.onToggle}
          />
          {/* Inert, so a press anywhere on the card picks it: the card's own buttons (text,
              snooze, call) would otherwise act on one person in the middle of picking many. */}
          <div inert className="min-w-0 flex-1">
            {children}
          </div>
        </div>
      </div>
    )
  return (
    <div
      // Finger drags go through `useTouchCardDrag`; a native one starting on the same long
      // press would cancel it.
      draggable={!(liftable && coarsePointer)}
      data-board-card={liftable ? id : undefined}
      // The id travels on the drag itself: a drop handler reading React state can see a stale value.
      onDragStart={(event) => {
        event.dataTransfer.setData(`text/plain`, id ?? ``)
        event.dataTransfer.effectAllowed = `move`
        onDragStart()
      }}
      // A drop on a real target clears the board's dragging id itself; this covers a release
      // over nothing, which fires no `onDrop`.
      onDragEnd={onDragEnd}
      // With an href the link owns opening, so a modified click can reach the browser.
      onClick={href ? undefined : onOpen}
      onPointerDown={onPress}
      className={cn(
        // `shrink-0` is load-bearing: a lane is a flex scroller, and a flex child shrinks by
        // default, squashing every card once they overflow the lane.
        `group shrink-0 cursor-pointer rounded-xl border border-default bg-surface shadow-elevation-1 transition`,
        dragging ? `opacity-40` : `hover:-translate-y-0.5 hover:border-strong hover:shadow-elevation-4`,
        // oxlint-disable-next-line shadcn/no-arbitrary-values -- no theme token for touch-callout suppression; verbatim from BoardCard/ManualListCard, part of the DOM contract Tasks 3 and 5 rely on
        liftable && `select-none [-webkit-touch-callout:none]`,
        className,
      )}
    >
      {actions && (
        <span
          data-card-actions
          onClick={(event) => event.stopPropagation()}
          className={cn(
            `absolute right-1 top-1 z-10 transition`,
            dragging
              ? `pointer-events-none opacity-0`
              : `opacity-0 has-[:focus-visible]:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100`,
          )}
        >
          {actions}
        </span>
      )}
      {href ? (
        <a
          href={href}
          // A plain <a> is natively draggable, and would swallow the card's own dragstart.
          draggable={false}
          // `ListSlotCard` clips the root with `overflow-hidden`, so an outset default focus
          // ring gets clipped away. Inset it so a keyboard tabbing across the board still sees it.
          className="block rounded-xl no-underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          onClick={(event) => {
            if (event.ctrlKey || event.metaKey) return
            event.preventDefault()
            onOpen()
          }}
        >
          {children}
        </a>
      ) : (
        children
      )}
    </div>
  )
}
